import React from "react";
import { Card, CardContent } from "./ui/card";
import { Star, Quote } from "lucide-react";

const TestimonialsSection = () => {
  const testimonials = [
    {
      content: "Grâce à EASY POWER, nous avons réduit notre facture d'électricité de **22% en six mois**. L'équipe a su analyser nos besoins industriels et négocier pour nous la meilleure offre du marché. Un service **efficace, rapide et sans engagement** !",
      author: "Dirigeant d'une PME métallurgique",
      rating: 5,
      savings: "22%",
      timeframe: "6 mois"
    },
    {
      content: "Nous cherchions un moyen de baisser nos charges énergétiques sans impacter nos résidents. EASY POWER nous a accompagnés pour renégocier tous nos contrats, et nous avons obtenu une réduction de **28% sur nos coûts annuels**. Professionnels et transparents !",
      author: "Syndic de copropriété",
      rating: 5,
      savings: "28%",
      timeframe: "1 an"
    },
    {
      content: "En tant que restaurateur, maîtriser mes dépenses est vital. Avec EASY POWER, j'ai trouvé une offre plus compétitive et découvert des optimisations de puissance souscrite. **Une vraie valeur ajoutée** pour mon business !",
      author: "Propriétaire restaurant",
      rating: 5,
      savings: "19%",
      timeframe: "8 mois"
    }
  ];

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`h-5 w-5 ${
          i < rating ? "text-yellow-400 fill-current" : "text-gray-300"
        }`}
      />
    ));
  };

  const formatContent = (content) => {
    return content.split('**').map((part, index) => {
      if (index % 2 === 1) {
        return <strong key={index} className="text-green-600">{part}</strong>;
      }
      return part;
    });
  };

  return (
    <section id="testimonials" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center justify-center p-4 bg-yellow-100 rounded-full mb-8">
            <Quote className="h-10 w-10 text-yellow-600" />
          </div>
          <h2 className="text-5xl font-bold text-gray-900 mb-6">
            Témoignages de nos clients
          </h2>
          <p className="text-2xl text-gray-600 max-w-4xl mx-auto">
            Découvrez les résultats concrets obtenus par nos clients grâce à notre expertise en courtage énergétique
          </p>
        </div>

        {/* Testimonials Grid */}
        <div className="grid lg:grid-cols-3 gap-10 mb-20">
          {testimonials.map((testimonial, index) => (
            <Card key={index} className="bg-white border-0 shadow-xl hover:shadow-2xl transition-all duration-300 group rounded-2xl">
              <CardContent className="p-10">
                {/* Rating */}
                <div className="flex items-center space-x-1 mb-8">
                  {renderStars(testimonial.rating)}
                </div>

                {/* Content */}
                <blockquote className="text-gray-700 leading-relaxed mb-8 italic text-lg">
                  "{formatContent(testimonial.content)}"
                </blockquote>

                {/* Results Badge */}
                <div className="flex items-center justify-between mb-8">
                  <div className="bg-emerald-100 px-6 py-3 rounded-full">
                    <span className="text-emerald-800 font-bold text-xl">
                      {testimonial.savings}
                    </span>
                    <span className="text-emerald-600 text-sm ml-2">d'économies</span>
                  </div>
                  <div className="text-gray-500">
                    en {testimonial.timeframe}
                  </div>
                </div>

                {/* Author */}
                <div className="border-t pt-8">
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 bg-gradient-to-r from-green-600 to-emerald-600 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold text-xl">
                        {testimonial.author.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900 text-lg">
                        {testimonial.author}
                      </div>
                      <div className="text-gray-500">
                        Client EASY POWER
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Image Background Section */}
        <div className="relative rounded-3xl overflow-hidden mb-20">
          <img
            src="https://images.unsplash.com/photo-1658242094232-0cacdec9fe55?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwyfHxlbmVyZ3klMjBlZmZpY2llbmN5fGVufDB8fHx8MTc1ODAzNjE5OXww&ixlib=rb-4.1.0&q=85"
            alt="Éoliennes - Énergie renouvelable"
            className="w-full h-[400px] object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-green-600/80 to-emerald-600/80"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center text-white">
              <div className="grid md:grid-cols-4 gap-12">
                <div>
                  <div className="text-5xl font-bold mb-2">2500+</div>
                  <div className="text-green-100 text-lg">Clients satisfaits</div>
                </div>
                <div>
                  <div className="text-5xl font-bold mb-2">25%</div>
                  <div className="text-green-100 text-lg">Économies moyennes</div>
                </div>
                <div>
                  <div className="text-5xl font-bold mb-2">100%</div>
                  <div className="text-green-100 text-lg">Indépendance</div>
                </div>
                <div>
                  <div className="text-5xl font-bold mb-2">32 ans</div>
                  <div className="text-green-100 text-lg">D'expérience</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <h3 className="text-4xl font-bold text-gray-900 mb-6">
            Rejoignez nos clients satisfaits
          </h3>
          <p className="text-2xl text-gray-600 mb-10 max-w-3xl mx-auto">
            Bénéficiez vous aussi de notre expertise pour réduire vos factures énergétiques
          </p>
          <button
            onClick={() => document.querySelector("#contact").scrollIntoView({ behavior: "smooth" })}
            className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-12 py-5 rounded-xl font-semibold text-xl transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            Demander votre audit gratuit
          </button>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;